"use client"

import { FormEvent, KeyboardEvent, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Send } from "lucide-react"
import LoadingSpinner from "@/components/shared/LoadingSpinner"

interface ChatInputProps {
  input: string
  setInput: (input: string) => void
  handleSubmit: (e: FormEvent) => void
  isLoading: boolean
  className?: string
}

export function ChatInput({
  input,
  setInput,
  handleSubmit,
  isLoading,
  className = "",
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-resize the textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = "0px"
      const scrollHeight = textarea.scrollHeight
      textarea.style.height = scrollHeight + "px"
    }
  }, [input])

  // Handle key presses for Ctrl+Enter to add new line and Enter to submit
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      if (e.ctrlKey) {
        // Add a new line when Ctrl+Enter is pressed
        setInput(`${input}\n\n`)
        e.preventDefault()
      } else if (!e.shiftKey) {
        // Submit the form when Enter is pressed without Shift
        if (input.trim()) {
          e.preventDefault()
          handleSubmit(e as unknown as FormEvent)
        }
      }
    }
  }
  return (
    <div className={`w-full p-2 mt-3 lg:my-4 ${className}`}>
      <form
        onSubmit={handleSubmit}
        className="flex flex-col  lg:flex-row lg:space-x-2 space-x-0 w-full items-end bg-white rounded-lg"
      >
        <span className="flex flex-col w-full">
          <p className="italic text-center text-sidebar-ring text-xs pb-0.5">
            This is an AI-based assistant. AI can make mistakes. Verify important information before
            relying on it.
          </p>
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message... (Ctrl+Enter for new line)"
            disabled={isLoading}
            className="flex-1 min-h-[40px] max-h-[200px] resize-none py-2"
            rows={1}
            autoFocus
          />
        </span>
        <Button
          type="submit"
          disabled={!input.trim() || isLoading}
          className="h-10 w-full lg:w-auto mt-2 lg:mt-0"
        >
          {isLoading ? (
            <LoadingSpinner
              spinnerClassName="text-muted-foreground"
              variant="default"
              loadingContent="Thinking..."
              size="xs"
            />
          ) : (
            <span className="flex gap-2.5 items-center pl-1.5 pr-2">
              <Send />
              Send
            </span>
          )}
        </Button>
      </form>
    </div>
  )
}
